import {
  getContactById,
  getReadinessFromAnswers,
  type DisplayContact,
  type EventType,
} from '@aikoa/common'
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useMemo } from 'react'
import { useUserId } from '~/components/user/SessionProvider'
import { supabase } from '~/lib/supabase'
import { trpc } from '~/lib/trpc'
import {
  getContactsPageForListing,
  searchContacts,
  getAllContacts,
  getAssetEventContacts,
  getContactInfo,
  getContactsByIds,
  getHasAnyContactsForFeedback,
  type ContactsSortBy,
  type SortDirection,
} from './readContacts'
import { deleteContact, updateContact, updateContactNotes } from './writeContacts'

export const CONTACTS_QUERY_KEY = (userId: string) => ({
  all: [userId, 'contacts'] as const,
  lists: [userId, 'contacts', 'list'] as const,
  list: (assetId?: string, count?: number) => [
    userId,
    'contacts',
    assetId ? 'asset' : 'all',
    assetId,
    count ?? 'all',
  ],
  detail: (contactId: string) => [userId, 'contacts', contactId] as const,
  info: (contactId: string) => [userId, 'contacts', contactId, 'info'] as const,
  assets: (contactId: string) => [userId, 'contacts', contactId, 'assets'] as const,
  selectedContacts: (selectedContactIdsKey: string) =>
    [userId, 'contacts', 'selected-contacts', selectedContactIdsKey] as const,
  hasAnyContactForFeedback: [userId, 'contacts', 'popover', 'has-any'] as const,
  listingPaginated: (sortBy: ContactsSortBy, sortDirection: SortDirection) =>
    [userId, 'contacts', 'listing-paginated', sortBy, sortDirection] as const,
  listingSearch: (sortBy: ContactsSortBy, sortDirection: SortDirection, search: string) =>
    [userId, 'contacts', 'listing-search', sortBy, sortDirection, search] as const,
})

function getStableContactIdsKey(contactIds: string[]) {
  return [...contactIds]
    .sort((firstContactId, secondContactId) => firstContactId.localeCompare(secondContactId))
    .join(',')
}

export type ContactsListSortBy = 'created_at' | 'name' | 'readiness'
export type ContactsListFilters = 'hasRespondedFeedback' | 'hasPendingFeedback' | 'hasNoFeedback'

type UseContactsPaginatedInput = {
  search?: string
  sortBy?: ContactsListSortBy
  sortDirection?: SortDirection
  filters?: Set<ContactsListFilters>
  enabled?: boolean
}

function applyContactsFilter(contacts: DisplayContact[], filters: Set<ContactsListFilters>) {
  return (
    contacts?.filter((contact) => {
      const feedbackItems = contact.feedback ?? []
      const hasAnyFeedback = feedbackItems.length > 0
      const hasResponded = feedbackItems.some((feedbackItem) => feedbackItem.completed_at !== null)
      const hasPending = hasAnyFeedback && !hasResponded

      if (filters.has('hasRespondedFeedback') && !hasResponded) {
        return false
      }

      if (filters.has('hasPendingFeedback') && !hasPending) {
        return false
      }

      if (filters.has('hasNoFeedback') && hasAnyFeedback) {
        return false
      }

      return true
    }) ?? []
  )
}

function getReadinessScore(contact: DisplayContact): number {
  return getReadinessFromAnswers(contact.answers) ?? 0
}

function sortContactsByReadiness(contacts: DisplayContact[], ascending: boolean) {
  return [...contacts].sort((firstContact, secondContact) => {
    const firstScore = getReadinessScore(firstContact)
    const secondScore = getReadinessScore(secondContact)

    return ascending ? firstScore - secondScore : secondScore - firstScore
  })
}

export function useContactsPaginated({
  search = '',
  sortBy = 'created_at',
  sortDirection = 'desc',
  filters = new Set<ContactsListFilters>(),
  enabled = true,
}: UseContactsPaginatedInput = {}) {
  const userId = useUserId()
  const trimmedSearch = search.trim()
  const hasSearch = trimmedSearch.length > 0

  const serverSortBy: ContactsSortBy = sortBy === 'readiness' ? 'updated_at' : sortBy

  const paginatedQuery = useInfiniteQuery({
    queryKey: CONTACTS_QUERY_KEY(userId).listingPaginated(serverSortBy, sortDirection),
    queryFn: ({ pageParam }) =>
      userId
        ? getContactsPageForListing({
            offset: pageParam,
            sortBy: serverSortBy,
            sortDirection,
          })
        : Promise.resolve({ contacts: [], nextOffset: null }),
    getNextPageParam: (lastPage) => lastPage.nextOffset ?? undefined,
    initialPageParam: 0,
    enabled: !!userId && enabled && !hasSearch,
  })

  const searchQuery = useQuery({
    queryKey: CONTACTS_QUERY_KEY(userId).listingSearch(serverSortBy, sortDirection, trimmedSearch),
    queryFn: () => (userId ? searchContacts({ search: trimmedSearch }) : Promise.resolve([])),
    enabled: !!userId && enabled && hasSearch,
    placeholderData: keepPreviousData,
  })

  const contacts = useMemo(() => {
    const baseContacts =
      (hasSearch
        ? searchQuery.data
        : paginatedQuery.data?.pages.flatMap((page) => page.contacts)) ?? []

    const filtered = applyContactsFilter(baseContacts, filters)

    if (sortBy === 'readiness') {
      return sortContactsByReadiness(filtered, sortDirection === 'asc')
    }

    return filtered
  }, [hasSearch, searchQuery.data, paginatedQuery.data?.pages, filters, sortBy, sortDirection])

  return {
    contacts,
    isLoading: hasSearch ? searchQuery.isLoading : paginatedQuery.isLoading,
    isFetching: hasSearch ? searchQuery.isFetching : paginatedQuery.isFetching,
    isSearching: hasSearch,
    hasNextPage: hasSearch ? false : !!paginatedQuery.hasNextPage,
    isFetchingNextPage: hasSearch ? false : paginatedQuery.isFetchingNextPage,
    fetchNextPage: () => {
      if (hasSearch || !paginatedQuery.hasNextPage || paginatedQuery.isFetchingNextPage) {
        return
      }

      void paginatedQuery.fetchNextPage()
    },
  }
}

export function useAddContact() {
  const queryClient = useQueryClient()
  const userId = useUserId()

  return useMutation(
    trpc.contacts.addContact.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: CONTACTS_QUERY_KEY(userId).all,
        })
      },
    })
  )
}

export function useDeleteContact() {
  const queryClient = useQueryClient()
  const userId = useUserId()

  return useMutation({
    mutationFn: deleteContact,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: CONTACTS_QUERY_KEY(userId).all,
      })
    },
  })
}

export function useUpdateContact() {
  const queryClient = useQueryClient()
  const userId = useUserId()

  return useMutation({
    mutationFn: updateContact,
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: CONTACTS_QUERY_KEY(userId).all,
      })

      if (variables.id) {
        void queryClient.invalidateQueries({
          queryKey: [userId, 'contacts', variables.id, 'assets'],
        })
      }
    },
  })
}

export function useUpdateContactNotes() {
  const queryClient = useQueryClient()
  const userId = useUserId()

  return useMutation({
    mutationFn: ({ contactId, notes }: { contactId: string; notes: string }) =>
      updateContactNotes(contactId, notes),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: CONTACTS_QUERY_KEY(userId).all,
      })

      void queryClient.invalidateQueries({
        queryKey: [userId, 'leads', variables.contactId],
      })
    },
  })
}

export function useContacts(assetId?: string) {
  const userId = useUserId()

  return useQuery({
    queryKey: CONTACTS_QUERY_KEY(userId).list(assetId),
    queryFn: () => (userId ? getAllContacts({ assetId }) : []),
    enabled: !!userId,
  })
}

export function useContact(contactId: string) {
  const userId = useUserId()

  return useQuery({
    queryKey: CONTACTS_QUERY_KEY(userId).detail(contactId),
    queryFn: () => (userId ? getContactById(contactId, supabase) : null),
    enabled: !!contactId && !!userId,
  })
}

export function useContactInfo(contactId: string) {
  const userId = useUserId()

  return useQuery({
    queryKey: CONTACTS_QUERY_KEY(userId).info(contactId),
    queryFn: () => (userId ? getContactInfo(contactId) : null),
    enabled: !!contactId && !!userId,
  })
}

export function useContactsByIds(contactIds: string[]) {
  const userId = useUserId()
  const contactIdsKey = useMemo(() => getStableContactIdsKey(contactIds), [contactIds])

  return useQuery({
    queryKey: CONTACTS_QUERY_KEY(userId).selectedContacts(contactIdsKey),
    queryFn: () => (userId ? getContactsByIds(contactIds) : []),
    enabled: !!userId && contactIds.length > 0,
    placeholderData: keepPreviousData,
  })
}

export function useHasAnyContactsForFeedback() {
  const userId = useUserId()

  return useQuery({
    queryKey: CONTACTS_QUERY_KEY(userId).hasAnyContactForFeedback,
    queryFn: () => (userId ? getHasAnyContactsForFeedback() : false),
    enabled: !!userId,
  })
}

export const ASSET_EVENT_CONTACTS_QUERY_KEY = (assetId: string, eventType: EventType) =>
  ['asset-event-contacts', assetId, eventType] as const

export function useAssetEventContact(assetId: string | undefined, eventType: EventType) {
  return useQuery({
    queryKey: ASSET_EVENT_CONTACTS_QUERY_KEY(assetId ?? '', eventType),
    queryFn: () => (assetId ? getAssetEventContacts(assetId, eventType) : null),
    enabled: !!assetId,
  })
}

export function useMySentContactRequests() {
  return useQuery(trpc.contacts.mySentContactRequests.queryOptions())
}

export function useLeadOwnerFromRequest(purchasedContactId?: string) {
  return useQuery(
    trpc.contacts.getLeadOwnerFromRequest.queryOptions(
      { purchasedContactId: purchasedContactId ?? '' },
      {
        enabled: !!purchasedContactId,
      }
    )
  )
}
